import React, { useCallback, useRef, useState } from 'react'

import { alpha, Box, useTheme } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'

import { Image, ProductModel, mediaPlateColor } from '@repo/ui'

/**
 * VRG-780 — a still photograph that becomes the real part when you hover it.
 *
 * The pattern is the hover GIF every storefront uses, except the second state is
 * a model: it keeps turning, it can be dragged to any angle, and on a phone it
 * can be placed in the room. Image 0 stays the photograph, deliberately — a grid
 * of eight live canvases is slower to paint, heavier to download and noisier to
 * look at than a grid of eight photographs, and nobody browsing a catalogue
 * asked for eight things to be moving at once.
 *
 * ## It is layer 2, not layer 1 (§47)
 *
 * The two halves are already shared: `Image` and `ProductModel` both live in
 * `@repo/ui`, and the three capabilities this needed from the viewer —
 * reduced-motion, a camera reset, a load-state callback — were added THERE
 * rather than worked around here, which is the direction §47 asks for. What is
 * left is the composition, and the tag shop is the only surface that wants it.
 * It moves down to `@repo/ui` on its second caller (§36), not before.
 *
 * ## Deferral is by NOT MOUNTING, which is stronger than `loading="lazy"`
 *
 * The viewer is not rendered at all until the first hover or focus. That defers
 * the GLB *and* the `@google/model-viewer` chunk, which dwarfs it — and
 * `loading="lazy"` would defer neither, since it gates only the model fetch and
 * only on an IntersectionObserver that a visible grid tile satisfies
 * immediately. Eight tiles at rest therefore cost exactly what they cost today:
 * eight `<img>`s.
 *
 * Once mounted it STAYS mounted, so the second hover is instant. What resets on
 * mouse-out is the camera, not the component.
 *
 * ## ⚠️ There is no touch path, and that is a limit rather than a decision
 *
 * Hover does not exist on a phone, so in the grid the model is a pointer-and-
 * keyboard enrichment; a touch user reaches it on the product page, where the
 * viewer is the page's subject and has room for its own controls. The obvious
 * fix — make the tile a button that toggles the model on tap — was rejected
 * because the tile's own job is to OPEN THE PRODUCT: a tap that showed a 3D view
 * instead would break the catalogue's primary action, and one that did both
 * would navigate away from the thing it had just revealed.
 */
/** Pointer travel past which a click is treated as a drag — see `onClick` below. */
const DRAG_SLOP_PX = 5

export interface ProductModelHoverProps {
  /** The still. Shown always, and the only thing shown when there is no model. */
  src: string
  /**
   * The GLB, or `undefined` for a product with no model.
   *
   * ⚠️ `undefined` MUST MEAN "none has been built", not "it might 404" — pass
   * `tagModelAsset()` / `primaryTagModel()`, which answer existence before
   * returning a URL. A URL that fails would mount a viewer, fetch the whole
   * model-viewer bundle and then report the failure in words, on a tile where
   * nothing had been promised.
   */
  modelSrc?: string
  /** Product title. Becomes the viewer's alt text and the focus affordance's label. */
  title?: string
  /** Both layers are sized by this, so the swap cannot shift the layout. */
  aspectRatio?: string
  /** Passed to the still, so the caller keeps its own corner radius. */
  imageSx?: SxProps<Theme>
  sx?: SxProps<Theme>
}

export const ProductModelHover = ({
  src,
  modelSrc,
  title,
  aspectRatio = '1 / 1',
  imageSx,
  sx,
}: ProductModelHoverProps) => {
  const theme = useTheme()
  /* Mounted at least once. Never goes back to false — see the docblock. */
  const [armed, setArmed] = useState(false)
  const [engaged, setEngaged] = useState(false)
  const [modelState, setModelState] = useState<'loading' | 'ready' | 'failed'>('loading')
  /* Bumped on disengage; `ProductModel` reads it as "re-aim the camera". */
  const [resetSignal, setResetSignal] = useState(0)
  const hostRef = useRef<HTMLDivElement | null>(null)

  /*
   * A FAILURE IS FINAL AND SILENT, which is the opposite of what `ProductModel`
   * does on its own and is right for a thumbnail.
   *
   * The viewer's own failure state is a bordered plate reading "The 3D view could
   * not be loaded on this device" — correct on a provenance surface, where a
   * model was promised and the absence of one is a fact about the record. Here
   * nothing was promised: the tile is a product photograph that happens to offer
   * more. So a failure drops the offer and leaves the photograph, and we remember
   * it so the next hover does not retry a fetch that has already failed.
   */
  const offerModel = Boolean(modelSrc) && modelState !== 'failed'
  const showModel = armed && engaged && modelState === 'ready'

  const engage = useCallback(() => {
    if (!modelSrc) return
    setArmed(true)
    setEngaged(true)
  }, [modelSrc])

  const disengage = useCallback(() => {
    setEngaged(false)
    setResetSignal((n) => n + 1)
  }, [])

  /*
   * A DRAG ON THE MODEL MUST NOT COUNT AS A CLICK ON THE CARD.
   *
   * The grid item above us navigates on click (`Shop.tsx` — `onClick={() =>
   * navigate(...)}` on the `Grid item`), so before this, dragging to turn a tag
   * opened the product page instead: the drag ended in a `click`, the click
   * bubbled, and the catalogue moved out from under the thing being dragged.
   * Caught in the browser, not in review — the guide already told buyers "drag
   * holds it" while it did not.
   *
   * The card's own job still wins for a plain click, which the docblock above
   * protects deliberately, so this cannot simply swallow every click on the
   * model. It measures instead: past a few pixels of travel the gesture was a
   * drag and the navigation is suppressed; below that it was a click and it
   * falls through untouched. Slop rather than zero because a real pointer moves
   * a pixel or two during an ordinary click.
   */
  const dragOrigin = useRef<{ x: number; y: number } | null>(null)
  const onPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    dragOrigin.current = { x: event.clientX, y: event.clientY }
  }, [])
  const onClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const origin = dragOrigin.current
    dragOrigin.current = null
    if (!origin) return
    const travelled = Math.hypot(event.clientX - origin.x, event.clientY - origin.y)
    if (travelled > DRAG_SLOP_PX) event.stopPropagation()
  }, [])

  /*
   * React's `onBlur` bubbles, so focus moving from this box INTO the viewer it
   * contains would fire blur-then-focus and flicker the still back for a frame.
   * `relatedTarget` is where focus is going; if that is still inside us, nothing
   * has been left.
   */
  const onBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      const next = event.relatedTarget as Node | null
      if (next && hostRef.current?.contains(next)) return
      disengage()
    },
    [disengage],
  )

  return (
    <Box
      ref={hostRef}
      /*
       * Focusable, with a label, so the spin is reachable without a pointer —
       * a hover-only affordance is simply absent for a keyboard user.
       *
       * ⚠️ A plain focusable region rather than a `<button>`, and the reason is
       * the same one the docblock gives for touch: the card around this box
       * navigates on click, so a button would have to either swallow that
       * navigation or fire alongside it. Focus alone reveals the model, and
       * Enter falls through to the card, which is what a keyboard user pressing
       * Enter on a product tile should get. No `tabIndex` at all when there is
       * no model, so nothing adds a dead tab stop.
       */
      {...(offerModel
        ? {
            tabIndex: 0,
            'aria-label': title
              ? `${title} — focus to see it in 3D`
              : 'Focus to see this product in 3D',
          }
        : {})}
      onMouseEnter={engage}
      onMouseLeave={disengage}
      onFocus={engage}
      onBlur={onBlur}
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio,
        borderRadius: 3,
        /*
         * THE PLATE, so the still and the model land on ONE surface.
         *
         * Both layers are transparent artwork — the still is a cut-out PNG and a GLB
         * renders on transparency — so without this each state showed whatever was
         * behind the tile, and the swap between them was a change of BACKGROUND as
         * well as a change of subject. Reading the token rather than a hex is what
         * keeps the tile agreeing with the cards around it in both modes
         * (theme/mediaPlate.ts).
         */
        backgroundColor: mediaPlateColor(theme),
        overflow: 'hidden',
        '&:focus-visible': {
          /* §31's field treatment, which is the house focus signal: the brand
             border plus its 6% ring. Only on `:focus-visible`, so a mouse click
             that happens to land here does not draw a ring nobody asked for. */
          outline: `1px solid ${theme.palette.brand[600]}`,
          boxShadow: `0 0 0 3px ${alpha(theme.palette.brand[600], 0.06)}`,
        },
        ...sx,
      }}
    >
      {/*
        Both layers are absolutely positioned in one ratio-sized box, so the swap
        is a cross-fade in place and the card's height never depends on which
        layer is showing (§19's footprint parity, applied to a state change
        rather than to a load).

        The still fades OUT rather than staying under the model: a GLB renders on
        transparency, so leaving the photograph behind it would show two tags at
        once at slightly different angles.
      */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: showModel ? 0 : 1,
          transition: 'opacity 180ms ease',
        }}
      >
        <Image src={src} alt={title} sx={{ borderRadius: 3, ...imageSx }} />
      </Box>
      {armed && offerModel && modelSrc && (
        <Box
          onPointerDown={onPointerDown}
          onClick={onClick}
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: showModel ? 1 : 0,
            transition: 'opacity 180ms ease',
            /* While it is invisible it must not eat the pointer, or the card
               under it would stop being clickable through the tile. */
            pointerEvents: showModel ? 'auto' : 'none',
          }}
        >
          <ProductModel
            src={modelSrc}
            title={title}
            aspectRatio={aspectRatio}
            surface="bare"
            /* 30deg/s, not the component's ambient 12. The spin here is ATTENDED
               — the user is holding a pointer on it and waiting to see the part
               turn — where the hero's figure is tuned to recede behind a page
               someone is reading. */
            rotationPerSecond="30deg"
            /*
             * Spin only while VISIBLE. The viewer stays mounted after the first
             * hover so the second one is instant, but a mounted viewer keeps
             * running its render loop whether or not anyone can see it — so a
             * grid someone has moved a pointer across would be left with seven
             * invisible canvases animating for the rest of the page's life.
             * Gating the attribute parks them; re-engaging restarts the spin on
             * the same frame the layer fades in. Measured in the browser, not
             * assumed: a disengaged tile read `auto-rotate="true"` before this.
             */
            autoRotate={showModel}
            /* 0, not 1000. The spin IS the reveal; a second of stillness first
               reads as a reveal that failed. */
            autoRotateDelayMs={0}
            /* No "drag to rotate" line: there is no room for it in a tile, and
               the card's own copy is doing other work. The product page carries
               the instruction. */
            showHint={false}
            resetSignal={resetSignal}
            onLoadStateChange={setModelState}
          />
        </Box>
      )}
    </Box>
  )
}
